import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { History, RefreshCw, ExternalLink, Loader2 } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import { SettingsCard } from '../settings/_shared';
import { listQbConnections, listSyncLog, retrySyncLogEntry } from '../../lib/api';

const PAGE = 25;

function _fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function _fmtAmount(n) {
  if (n === null || n === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(Number(n));
  } catch { return `$${Number(n).toFixed(2)}`; }
}

function qboDeepLink(entry, connectionMap) {
  if (!entry?.qb_transaction_id) return null;
  const conn = connectionMap[entry.connection_id];
  if (!conn) return null;
  const host = (conn.environment || 'sandbox') === 'production'
    ? 'https://qbo.intuit.com'
    : 'https://sandbox.qbo.intuit.com';
  const path = entry.transaction_type === 'Bill' ? 'bill' : 'purchase';
  return `${host}/app/${path}?txnId=${encodeURIComponent(entry.qb_transaction_id)}`;
}

/**
 * Phase 25.3 — Sync history (append-only publish log).
 *
 * Filters: connection + status. Infinite scroll via cursor. Retry button
 * on failed rows; the retry creates a new row and updates the parent's
 * `retried_by` back-reference server-side.
 */
export default function SyncLogSection({ onToast }) {
  const { colors } = useTheme();
  const [connections, setConnections] = useState([]);
  const [filters, setFilters] = useState({ connection_id: '', status: '' });
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async (append = false) => {
    setLoading(true);
    try {
      const data = await listSyncLog({
        connection_id: filters.connection_id || undefined,
        status: filters.status || undefined,
        limit: PAGE,
        cursor: append ? cursor : undefined,
      });
      setItems((prev) => (append ? [...prev, ...(data.items || [])] : (data.items || [])));
      setCursor(data.next_cursor || null);
    } catch (e) {
      onToast?.(`Could not load sync history: ${e.detail || 'network error'}`);
    } finally {
      setLoading(false);
    }
  }, [filters, cursor, onToast]);

  useEffect(() => {
    (async () => {
      const data = await listQbConnections().catch(() => ({ connections: [] }));
      setConnections(data.connections || []);
    })();
  }, []);

  useEffect(() => {
    setItems([]); setCursor(null);
    load(false);
  }, [filters.connection_id, filters.status]);

  const connectionMap = useMemo(() => {
    const m = {};
    for (const c of connections) m[c.id] = c;
    return m;
  }, [connections]);

  const onRetry = async (entryId) => {
    setRetrying(entryId);
    try {
      await retrySyncLogEntry(entryId);
      onToast?.('Retry succeeded');
      await load(false);
    } catch (e) {
      onToast?.(`Retry failed: ${typeof e.detail === 'string' ? e.detail : (e.detail?.message || 'error')}`);
      await load(false);
    } finally {
      setRetrying(null);
    }
  };

  return (
    <SettingsCard
      testid="qb-sync-log-section"
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <History size={16} /> Sync history
        </span>
      }
      description="Every publish attempt, success or failure, appears here. Failed rows can be retried — a new entry is appended and the original is preserved."
    >
      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <select
          data-testid="synclog-filter-conn"
          value={filters.connection_id}
          onChange={(e) => setFilters((f) => ({ ...f, connection_id: e.target.value }))}
          style={filterSel(colors)}
        >
          <option value="">All companies</option>
          {connections.map((c) => (
            <option key={c.id} value={c.id}>{c.company_name}</option>
          ))}
        </select>
        <select
          data-testid="synclog-filter-status"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          style={filterSel(colors)}
        >
          <option value="">All statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {items.length === 0 && !loading && (
        <div
          data-testid="synclog-empty"
          style={{
            padding: 18, borderRadius: 10,
            background: colors.bg, border: `1px dashed ${colors.border}`,
            color: colors.textMuted, fontSize: 13,
          }}
        >
          No sync activity yet — publish a receipt to see it here.
        </div>
      )}

      {items.length > 0 && (
        <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: colors.bg, color: colors.textMuted, fontWeight: 700, textAlign: 'left' }}>
                <th style={th()}>Date</th>
                <th style={th()}>Receipt</th>
                <th style={th()}>Vendor</th>
                <th style={{ ...th(), textAlign: 'right' }}>Amount</th>
                <th style={th()}>Type</th>
                <th style={th()}>Company</th>
                <th style={th()}>Status</th>
                <th style={th()}>Details</th>
                <th style={th()} />
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const link = qboDeepLink(e, connectionMap);
                const conn = connectionMap[e.connection_id];
                return (
                  <tr key={e.id} data-testid={`synclog-row-${e.id}`} style={{ borderTop: `1px solid ${colors.border}` }}>
                    <td style={td()}>{_fmtDate(e.created_at)}</td>
                    <td style={td()}>
                      <Link
                        to={`/app/receipts/${e.receipt_id}`}
                        style={{ color: colors.accent, fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600, textDecoration: 'none' }}
                      >
                        {e.receipt_id.slice(0, 8)}
                      </Link>
                    </td>
                    <td style={{ ...td(), color: colors.text }} data-testid={`synclog-vendor-${e.id}`}>
                      {e.vendor || <span style={{ color: colors.textMuted }}>—</span>}
                    </td>
                    <td style={{ ...td(), textAlign: 'right', color: colors.text, fontVariantNumeric: 'tabular-nums' }} data-testid={`synclog-amount-${e.id}`}>
                      {_fmtAmount(e.amount)}
                    </td>
                    <td style={td()}>
                      <TypePill type={e.transaction_type} />
                    </td>
                    <td style={td()}>{conn?.company_name || e.realm_id?.slice(0, 8)}</td>
                    <td style={td()}>
                      <StatusBadge status={e.status} />
                    </td>
                    <td style={{ ...td(), color: e.status === 'failed' ? '#8C1E12' : colors.textMuted, maxWidth: 280 }} title={e.error || ''}>
                      {e.status === 'failed' ? (e.error || 'error').slice(0, 80) : (
                        <>
                          QB#{e.qb_transaction_id}
                          {link && (
                            <a href={link} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 6, color: colors.accent }}>
                              <ExternalLink size={11} style={{ verticalAlign: 'middle' }} />
                            </a>
                          )}
                        </>
                      )}
                    </td>
                    <td style={td()}>
                      {e.status === 'failed' && !e.retried_by && (
                        <button
                          type="button"
                          data-testid={`synclog-retry-${e.id}`}
                          onClick={() => onRetry(e.id)}
                          disabled={retrying === e.id}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '4px 10px', borderRadius: 7,
                            background: 'transparent', color: colors.accent,
                            border: `1px solid ${colors.accent}`,
                            fontSize: 11, fontWeight: 700, cursor: retrying === e.id ? 'wait' : 'pointer',
                          }}
                        >
                          {retrying === e.id ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                          {retrying === e.id ? 'Retrying…' : 'Retry'}
                        </button>
                      )}
                      {e.retried_by && (
                        <span style={{ fontSize: 10.5, color: colors.textMuted, fontStyle: 'italic' }}>retried</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {cursor && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            data-testid="synclog-load-more"
            onClick={() => load(true)}
            disabled={loading}
            style={{
              padding: '7px 12px', borderRadius: 8,
              background: 'transparent', color: colors.textMuted,
              border: `1px solid ${colors.border}`, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </SettingsCard>
  );
}

function TypePill({ type }) {
  const bg = type === 'Bill' ? '#EEF4FF' : '#E7F7EE';
  const fg = type === 'Bill' ? '#1D4ED8' : '#117A3C';
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 999,
      background: bg, color: fg, fontSize: 10.5, fontWeight: 700,
    }}>
      {type || 'Purchase'}
    </span>
  );
}
function StatusBadge({ status }) {
  const map = {
    success: { bg: '#E7F7EE', fg: '#117A3C', label: 'Success' },
    failed: { bg: '#FDECEC', fg: '#B42318', label: 'Failed' },
  };
  const r = map[status] || map.success;
  return (
    <span data-testid={`synclog-status-${status}`} style={{
      padding: '2px 8px', borderRadius: 999,
      background: r.bg, color: r.fg, fontSize: 10.5, fontWeight: 700,
    }}>
      {r.label}
    </span>
  );
}
function th() {
  return { padding: '9px 12px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em' };
}
function td() {
  return { padding: '9px 12px', verticalAlign: 'middle' };
}
function filterSel(colors) {
  return {
    padding: '6px 10px', borderRadius: 8,
    background: colors.bg, color: colors.text,
    border: `1px solid ${colors.border}`, fontSize: 12.5, fontWeight: 600,
    minWidth: 160,
  };
}
